import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/auth/AuthContext';
import { getCrewServices, DayServiceSummary } from '../../lib/services/serviceReportService';

const MyServices: React.FC = () => {
  const { userProfile } = useAuth();
  const [daySummaries, setDaySummaries] = useState<DayServiceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDate, setSearchDate] = useState<string>('');
  const [staffId, setStaffId] = useState<string | null>(null);

  // Get staff ID from user profile
  useEffect(() => {
    const fetchStaffId = async () => {
      if (!userProfile?.id) return;

      const { data, error } = await supabase
        .from('team')
        .select('id')
        .eq('user_id', userProfile.id)
        .single();

      if (error) {
        console.error('Error fetching staff ID:', error);
        return;
      }

      if (data) {
        setStaffId(data.id);
      }
    };

    fetchStaffId();
  }, [userProfile]);

  // Fetch services when staffId is available
  useEffect(() => {
    if (staffId) {
      fetchServices();
    }
  }, [staffId]);

  const fetchServices = async (date?: Date) => {
    if (!staffId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await getCrewServices(staffId, date);
      setDaySummaries(data);
    } catch (err: any) {
      console.error('Error fetching services:', err);
      setError(err.message || 'Failed to load services');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchDate = () => {
    if (searchDate) {
      fetchServices(new Date(searchDate));
    } else {
      fetchServices(); // Reset to last 3 days
    }
  };

  const handleClearSearch = () => {
    setSearchDate('');
    fetchServices();
  };

  if (isLoading && !staffId) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-[#3A5E63] mb-4">My Services</h2>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#3A5E63]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <h2 className="text-xl font-semibold text-[#3A5E63]">My Services</h2>

        {/* Date Search */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
          />
          <button
            onClick={handleSearchDate}
            className="px-3 py-1.5 bg-[#3A5E63] text-white rounded-md text-sm hover:bg-[#2d4a4e] transition-colors"
          >
            Search
          </button>
          {searchDate && (
            <button
              onClick={handleClearSearch}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#3A5E63]"></div>
        </div>
      ) : daySummaries.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No paid services found for this period
        </div>
      ) : (
        <div className="space-y-6">
          {daySummaries.map((day) => (
            <div key={day.date} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Day Header */}
              <div className="bg-gray-50 px-4 py-3 flex justify-between items-center">
                <div>
                  <span className="font-semibold text-gray-800">{day.dateLabel}</span>
                  <span className="text-sm text-gray-500 ml-2">
                    ({day.total_services} {day.total_services === 1 ? 'service' : 'services'})
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-600">Tips: </span>
                  <span className={`font-semibold ${day.total_tips > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                    ${day.total_tips.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Appointments */}
              <div className="divide-y divide-gray-100">
                {day.appointments.map((apt) => (
                  <div key={apt.id} className="px-4 py-3 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{apt.customer_name}</span>
                          <span className="text-xs text-gray-400">
                            {format(new Date(apt.start_time), 'h:mm a')}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {apt.services.map((s) => s.name).join(', ')}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm text-gray-600">
                          ${apt.total_price.toFixed(2)}
                        </div>
                        <div className={`text-sm font-medium ${apt.tip_amount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          {apt.tip_amount > 0 ? (
                            <>
                              +${apt.tip_amount.toFixed(2)} tip
                              {apt.tip_percentage && (
                                <span className="text-xs ml-1">({apt.tip_percentage}%)</span>
                              )}
                            </>
                          ) : (
                            'No tip'
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 text-center">
        {searchDate ? 'Showing results for selected date' : 'Showing last 3 days of paid services'}
      </div>
    </div>
  );
};

export default MyServices;
